export const setItemToLocalStorage = function (localStorageKey: string, value: unknown) {
  if (typeof localStorageKey !== "string") return null;
  localStorage.setItem(localStorageKey, JSON.stringify(value));
};

export const getItemFromLocalStorage = function (localStorageKey: string) {
  if (typeof localStorageKey !== "string") return null;
  const item = localStorage.getItem(localStorageKey);
  return item !== null ? JSON.parse(item) : null;
};

export const readValidatedJsonFromLocalStorage = <T>(
  key: string,
  validate: (value: unknown) => value is T,
  fallback: T
): T => {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return fallback;
    const value: unknown = JSON.parse(stored);
    return validate(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

export const writeValidatedJsonToLocalStorage = <T>(
  key: string,
  value: unknown,
  validate: (value: unknown) => value is T
): boolean => {
  try {
    if (!validate(value)) return false;
    const stored = JSON.stringify(value);
    if (stored === undefined) return false;
    localStorage.setItem(key, stored);
    return true;
  } catch {
    return false;
  }
};

const isJsonRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const readJsonRecord = (storageKey: string): Record<string, unknown> => {
  const stored = localStorage.getItem(storageKey);
  if (stored === null) return {};

  try {
    const record: unknown = JSON.parse(stored);
    return isJsonRecord(record) ? record : {};
  } catch {
    return {};
  }
};

export const readValidatedJsonEntry = <T>(
  storageKey: string,
  entryKey: string,
  { validate, fallback }: { validate: (value: unknown) => value is T; fallback: T }
): T => {
  try {
    const record = readJsonRecord(storageKey);
    if (!Object.hasOwn(record, entryKey)) return fallback;
    const value = record[entryKey];
    return validate(value) ? value : fallback;
  } catch {
    return fallback;
  }
};

export const writeValidatedJsonEntry = <T>(
  storageKey: string,
  entryKey: string,
  { value, validate }: { value: unknown; validate: (value: unknown) => value is T }
): boolean => {
  try {
    if (!validate(value)) return false;
    const record = readJsonRecord(storageKey);
    return writeValidatedJsonToLocalStorage(storageKey, { ...record, [entryKey]: value }, isJsonRecord);
  } catch {
    return false;
  }
};
